<template>
  <InfoBlock>
    <template v-slot:header>Процент качественных объектов</template>
    <template v-slot:default>
      <FetchJson :url="url" @fetched="onDataFetched">
        <div :key="componentKey" :style="'height: ' + chartAreaHeight + 'px'">
          <VueApexCharts
            :options="chartOptions"
            :series="seriesToShow"
            height="100%"
          ></VueApexCharts>
        </div>
      </FetchJson>
    </template>
    <template v-slot:footer>
      <button
        class="button button--outline"
        @click="onShowMore"
        v-show="canShowMore"
      >
        {{
          showMore
            ? "Скрыть остальные территории"
            : "Показать остальные территории"
        }}
      </button>
    </template>
  </InfoBlock>
</template>

<script>
import { ref, toRefs, computed } from "vue";
import { dateToString } from "@/modules/date";
import { sortStackedSeriesData } from "@/modules/chart";
import useConstants from "@/hooks/constants";
import VueApexCharts from "vue3-apexcharts";

export default {
  props: {
    filter: {
      type: Object,
      required: true,
    },
  },
  components: { VueApexCharts },
  setup(props) {
    const constants = useConstants().charts;
    const componentKey = ref(Date.now());
    const filter = toRefs(props.filter);
    const isDataEmpty = ref(false);
    const fillColors = ref(["#518CFF"]);
    const filterType = ref("lighten");
    const url = computed(
      () =>
        `objects/valid-object-quantity-by-territory?from=${dateToString(
          filter.from.value
        )}&to=${dateToString(filter.to.value)}&territory=${
          filter.territory.value.alias || ""
        }`
    );
    const canShowMore = computed(() => {
      return categories.value.length > constants.itemCountToShow;
    });
    const showMore = ref(false);
    const series = ref([]);
    const categories = ref([]);
    const itemCountToShow = computed(() => {
      if (canShowMore.value)
        return showMore.value
          ? categories.value.length
          : constants.itemCountToShow;

      return categories.value.length;
    });
    const seriesToShow = computed(() => {
      return showMore.value
        ? series.value
        : [
            {
              data: series.value[0]
                ? series.value[0].data.slice(0, itemCountToShow.value)
                : [],
            },
          ];
    });
    const categoriesToShow = computed(() => {
      return showMore.value
        ? categories.value
        : categories.value.slice(0, itemCountToShow.value);
    });
    const chartAreaHeight = computed(
      () =>
        constants.barHeight * itemCountToShow.value +
        constants.validDocumentsFooterChartHeight
    );
    const maxWidth = computed(() => {
      return Math.max(...seriesToShow.value[0].data) * 1.05;
    });
    const chartOptions = computed(() => {
      return {
        chart: {
          type: "bar",
          toolbar: {
            show: false,
          },
          zoom: {
            enabled: false,
          },
          animations: {
            enabled: true,
            easing: "easein",
            speed: 200,
            animateGradually: {
              enabled: true,
              delay: 150,
            },
            dynamicAnimation: {
              enabled: true,
              speed: 350,
            },
          },
        },
        tooltip: {
          enabled: false,
        },
        grid: {
          show: false,
        },
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 0, //todo вычислять от данных
            barHeight: "50%", //todo вычислять от данных
            dataLabels: {
              position: "top",
            },
          },
        },
        dataLabels: {
          enabledOnSeries: [0],
          style: {
            colors: ["#465362"],
            fontSize: "14px",
            fontWeight: "regular",
          },
          offsetX: 15,
          textAnchor: "start",
          formatter: function (val) {
            if (isDataEmpty.value) return "%";
            return `${val}%`;
          },
        },
        colors: ["#518CFF"],
        xaxis: {
          categories: categoriesToShow.value,
          labels: {
            show: false,
          },
          max: maxWidth.value,
        },
        yaxis: {
          labels: {
            style: {
              colors: ["#465362"],
              fontSize: "16px",
              fontFamily: "Source Sans Pro, Tahoma, sans-serif",
              fontWeight: 400,
              cssClass: "apexcharts-yaxis-label",
            },
            align: "left",
            offsetX: 8,
            maxWidth: 250,
            formatter: (value) =>
              value.length >= 30 ? `${value.substr(0, 29)}...` : value,
          },
        },
        fill: {
          colors: fillColors.value, // красим график
        },
        states: {
          hover: {
            filter: {
              type: filterType.value, // график не пропадает при наведении
            },
          },
        },
      };
    });
    const onDataFetched = function (data) {
      if (fetchedDataIsEmpty(data)) {
        const categoryCount = data.categories.length;
        var values = [];

        for (var j = 0; j < categoryCount; j++) {
          values.push(100);
        }

        for (var i = 0; i < data.series.length; i++) {
          data.series[i].values = values;
        }
      }

      data = sortStackedSeriesData(data);

      categories.value = data.categories;
      series.value = [
        { data: data.series.length ? data.series[0].values : [] },
      ];

      componentKey.value = Date.now(); // Tell Vue to re-create the component.
    };

    function fetchedDataIsEmpty(data) {
      var isEmpty = data.series.every((x) => x.values.every((y) => y == 0));

      if (isEmpty) {
        isDataEmpty.value = true;
        filterType.value = "none";
        fillColors.value = ["#D5D5D5"];
        return true;
      }

      isDataEmpty.value = false;
      filterType.value = "lighten";
      fillColors.value = ["#518CFF"];
      return false;
    }

    return {
      componentKey,
      chartOptions,
      seriesToShow,
      canShowMore,
      showMore,
      chartAreaHeight,
      url,
      onDataFetched,
    };
  },
  created() {
    this.componentKey = Date.now(); // Tell Vue to re-create the component.
  },
  methods: {
    onShowMore() {
      this.showMore = !this.showMore;
      this.componentKey = Date.now(); // Tell Vue to re-create the component.
    },
  },
};
</script>

<style lang="scss">
.indicators.layout--horizontal {
  align-items: stretch;
}
</style>
